import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,

  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close, Edit, Save, Cancel, Visibility, Delete, Restore } from '@mui/icons-material';
import { CustomerWithStats } from '../../types/Customer';
import { PaymentLink } from '../../types/PaymentLink';
import {
  getCustomerPaymentLinks,
  updateCustomer,
  softDeleteCustomer,
  restoreCustomer,
} from '../../services/customerService';
import { formatCurrency, formatDateTime, validateEmail, validatePhoneSimple } from '../../utils/validators';
import { toast } from 'react-toastify';
import StatusChip from '../common/StatusChip';
import { getCurrentUser } from '../../services/authService';

interface CustomerDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  customer: CustomerWithStats | null;
  onUpdated: () => void;
  onViewPaymentLink?: (linkId: string) => void;
}

const CustomerDetailsDialog: React.FC<CustomerDetailsDialogProps> = ({
  open,
  onClose,
  customer,
  onUpdated,
  onViewPaymentLink,
}) => {
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});

  const loadPaymentLinks = useCallback(async () => {
    if (!customer) return;

    setLoading(true);
    try {
      const links = await getCustomerPaymentLinks(customer.id);
      setPaymentLinks(links);
    } catch (error) {
      console.error('Error loading payment links:', error);
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if (customer && open) {
      loadPaymentLinks();
      setIsEditing(false);
      setEditForm({
        name: customer.name,
        email: customer.email,
        phone: customer.phone || '',
        notes: customer.notes || '',
      });
      setErrors({});
    }
  }, [customer, open, loadPaymentLinks]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (customer) {
      setEditForm({
        name: customer.name,
        email: customer.email,
        phone: customer.phone || '',
        notes: customer.notes || '',
      });
    }
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!editForm.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!editForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(editForm.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (editForm.phone && !validatePhoneSimple(editForm.phone)) {
      newErrors.phone = 'Invalid phone format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!customer) return;

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const currentUser = getCurrentUser();
      await updateCustomer(
        customer.id,
        {
          name: editForm.name.trim(),
          email: editForm.email.trim(),
          phone: editForm.phone.trim() || undefined,
          notes: editForm.notes.trim() || undefined,
        },
        currentUser?.email || undefined
      );

      toast.success('Customer updated successfully');
      setIsEditing(false);
      onUpdated();
    } catch (error: unknown) {
      console.error('Error updating customer:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to update customer'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!customer) return;

    const confirmMessage = customer.deleted
      ? 'Restore this customer?'
      : 'Delete this customer? They can be restored later.';

    if (!window.confirm(confirmMessage)) return;

    try {
      const currentUser = getCurrentUser();
      if (customer.deleted) {
        await restoreCustomer(customer.id);
        toast.success('Customer restored');
      } else {
        await softDeleteCustomer(customer.id, currentUser?.email || undefined);
        toast.success('Customer deleted');
      }
      onUpdated();
      onClose();
    } catch (error) {
      console.error('Error toggling delete:', error);
      toast.error('Failed to update customer');
    }
  };

  const handleFormChange = (field: keyof typeof editForm) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Customer Details</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Customer Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom color="primary">
              Customer Information
            </Typography>

            {isEditing ? (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editForm.name}
                  onChange={handleFormChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={saving}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editForm.email}
                  onChange={handleFormChange('email')}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={saving}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={editForm.phone}
                  onChange={handleFormChange('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  disabled={saving}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={editForm.notes}
                  onChange={handleFormChange('notes')}
                  disabled={saving}
                />
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {customer.name}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {customer.email}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {customer.phone || '-'}
                </Typography>

                {customer.notes && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {customer.notes}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Grid>

          {/* Statistics */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom color="primary">
              Statistics
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {customer.paymentLinksCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Payment Links
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {customer.totalPayments}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completed Payments
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                      {formatCurrency(customer.totalAmount, 'EUR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Revenue
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Last Payment
                </Typography>
                <Typography variant="body1">
                  {customer.lastPaymentDate
                    ? formatDateTime(customer.lastPaymentDate.toDate())
                    : 'No payments yet'}
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Customer Since
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(customer.createdAt.toDate())}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Payment History */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom color="primary">
              Payment History ({paymentLinks.length})
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : paymentLinks.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No payment links found for this customer
              </Alert>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentLinks.map((link) => (
                      <TableRow key={link.id}>
                        <TableCell>{link.invoiceNumber || link.linkId}</TableCell>
                        <TableCell>
                          <StatusChip status={link.status} />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(link.invoiceAmount, link.currency)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'success.main' }}
                          >
                            {formatCurrency(link.totalPaid || 0, link.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDateTime(link.createdAt.toDate())}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {onViewPaymentLink && (
                            <IconButton
                              size="small"
                              onClick={() => onViewPaymentLink(link.id)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 1 }}>
          <Box>
            <Button
              startIcon={customer.deleted ? <Restore /> : <Delete />}
              onClick={handleDelete}
              color={customer.deleted ? 'success' : 'error'}
            >
              {customer.deleted ? 'Restore' : 'Delete'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditing ? (
              <>
                <Button startIcon={<Cancel />} onClick={handleCancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" startIcon={<Edit />} onClick={handleEditClick}>
                  Edit
                </Button>
              </>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDetailsDialog;
